/**
 * Pure derivations for the multi-agent IDE right-panels.
 *
 * COPIED from pages/dev-agent/components/rightPanels/derivePanelData.ts and
 * rewired to the multi-agent feed: the dev-agent version derived everything from
 * `FeedItem[]` (devAgentStore), which this store does NOT have. The multi-agent
 * run instead owns a typed `testRuns: TestRun[]` slice, so this module maps that
 * slice into the row shape `TestsPanel` renders — preserving the dev-agent panel
 * code unchanged. No dev-agent import.
 */
import type { CoveredClass, TestRun } from "../store/multiAgentIdeStore";

/** Row shape consumed by TestsPanel (mirrors the dev-agent FeedItem `data` it read). */
export interface TestPanelRow {
  /** Stable key for expand/selection — the test class name. */
  id: string;
  data: {
    className: string | null;
    /** Coverage % of the preferred (production) covered class, if any. */
    coveragePct?: number | null;
    coverage?: number | null;
    uncoveredCount?: number | null;
    status?: string;
    summary?: string;
    coveredClasses: CoveredClass[];
    /** Preferred production class name (used to pick the primary coverage target). */
    productionClass?: string | null;
  };
}

function preferredCoveredClass(
  testRun: TestRun,
): CoveredClass | null {
  const covered = testRun.coveredClasses || [];
  // The "production" class for a *Test class is conventionally the same name
  // without the trailing "Test" — prefer an exact match, else the first entry.
  // Drop a trailing "Test" suffix (case-insensitive) to recover the base name.
  const rawBase = testRun.className || testRun.testClassName || "";
  const base =
    rawBase.slice(-4).toLowerCase() === "test" ? rawBase.slice(0, -4) : rawBase;
  if (base) {
    const exact = covered.find((c) => c.name === base);
    if (exact) return exact;
  }
  return covered[0] || null;
}

/**
 * Map the multi-agent store's `testRuns` into the rows TestsPanel renders.
 * Newest run first (the store upserts by testClassName and keeps insertion
 * order; we reverse so the most-recent run is at the top).
 */
export function deriveTestPanelRows(testRuns: TestRun[]): TestPanelRow[] {
  return [...testRuns].reverse().map((run) => {
    const primary = preferredCoveredClass(run);
    const coveragePct = primary?.coverage_pct ?? null;
    return {
      id: run.testClassName,
      data: {
        className: run.className ?? run.testClassName,
        productionClass: primary?.name ?? null,
        coveragePct,
        coverage: coveragePct,
        uncoveredCount: primary ? primary.num_uncovered : null,
        status: run.success ? "passed" : "failed",
        summary: run.summary,
        coveredClasses: run.coveredClasses || [],
      },
    };
  });
}
